import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Plus, Upload, CalendarDays } from "lucide-react";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import toast from "react-hot-toast";
import { FormField } from "@/components/ui";
import GenericTable from "@/components/ui/GenericTable";
import MasterModal from "@/components/masters/MasterModal";
import { useScreenPermission } from "@/hooks/useScreenPermission";
import {
  getHolidays, createHoliday, createHolidayFromExcel,
  updateHoliday, deleteHoliday,
} from "@/services/holidays";

// ── Constants ────────────────────────────────────────────────────────────────
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const MONTHS = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];
const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 2 }, (_, i) => CURRENT_YEAR - (1 - i));

// ── Zod Schema ────────────────────────────────────────────────────────────────
const holidaySchema = z.object({
  Date:                 z.string().min(1, "Date is required"),
  Name_Of_The_Festival: z.string().min(1, "Festival name is required"),
  Day_Of_Week:          z.string().min(1, "Day is required"),
  Month:                z.string().min(1, "Month is required"),
  Year:                 z.number().int(),
});

type HolidayFormData = z.infer<typeof holidaySchema>;

// ── Main component ────────────────────────────────────────────────────────────
interface Props {
  viewOnly?: boolean;
}

export default function HolidaysView({ viewOnly = false }: Props) {
  const { canCreate, canUpdate, canDelete } = useScreenPermission("holidays");

  const showAdd    = !viewOnly && canCreate;
  const showEdit   = !viewOnly && canUpdate;
  const showDelete = !viewOnly && canDelete;

  const [holidays, setHolidays]       = useState<any[]>([]);
  const [loading, setLoading]         = useState(false);
  const [yearFilter, setYearFilter]   = useState(CURRENT_YEAR);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<any | null>(null);
  const [addTab, setAddTab]           = useState<"form" | "excel">("form");
  const [excelFile, setExcelFile]     = useState<File | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);
  const [deleting, setDeleting]       = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const methods = useForm<HolidayFormData>({
    resolver: zodResolver(holidaySchema),
    defaultValues: { Date: "", Name_Of_The_Festival: "", Day_Of_Week: "", Month: "", Year: CURRENT_YEAR },
  });

  // Auto-fill Day / Month / Year when Date field changes
  const watchDate = methods.watch("Date");
  useEffect(() => {
    if (!watchDate) return;
    const d = new Date(watchDate);
    if (!isNaN(d.getTime())) {
      methods.setValue("Day_Of_Week", d.toLocaleDateString("en-US", { weekday: "long" }));
      methods.setValue("Month",       d.toLocaleDateString("en-US", { month: "long" }));
      methods.setValue("Year",        d.getFullYear());
    }
  }, [watchDate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getHolidays(yearFilter);
      setHolidays(Array.isArray(data) ? data : (data as any)?.results ?? []);
    } catch {
      // toast.error("Failed to load holidays");
    } finally {
      setLoading(false);
    }
  }, [yearFilter]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEditingItem(null);
    methods.reset({ Date: "", Name_Of_The_Festival: "", Day_Of_Week: "", Month: "", Year: CURRENT_YEAR });
    setAddTab("form");
    setExcelFile(null);
    setIsModalOpen(true);
  };

  const openEdit = (row: any) => {
    setEditingItem(row);
    methods.reset({
      Date:                 row.Date,
      Name_Of_The_Festival: row.Name_Of_The_Festival,
      Day_Of_Week:          row.Day_Of_Week,
      Month:                row.Month,
      Year:                 Number(row.Year),
    });
    setAddTab("form");
    setIsModalOpen(true);
  };

  const handleSubmit = methods.handleSubmit(async (data) => {
    if (addTab === "excel") {
      if (!excelFile) { toast.error("Please select an Excel file"); return; }
      try {
        const res = await createHolidayFromExcel(excelFile);
        if ((res as any).status === 200 || (res as any).status === 201) {
          toast.success("Holidays imported successfully");
          setIsModalOpen(false);
          load();
        } else {
          toast.error("Import failed");
        }
      } catch { toast.error("Import failed"); }
      return;
    }

    try {
      const payload = data as HolidayFormData;
      const res = editingItem
        ? await updateHoliday(editingItem.id, payload)
        : await createHoliday(payload);
      const status = (res as any).status;
      if (status === 200 || status === 201) {
        toast.success(editingItem ? "Holiday updated" : "Holiday added");
        setIsModalOpen(false);
        load();
      } else {
        toast.error("Failed to save holiday");
      }
    } catch { toast.error("Failed to save holiday"); }
  });

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await deleteHoliday(deleteTarget.id);
      if ((res as any).status === 200 || (res as any).status === 204) {
        toast.success("Holiday deleted");
        setDeleteTarget(null);
        load();
      } else {
        toast.error("Failed to delete");
      }
    } catch { toast.error("Failed to delete"); }
    finally { setDeleting(false); }
  };

  const columns = useMemo(() => [
    { key: "Date",                 label: "Date" },
    { key: "Name_Of_The_Festival", label: "Festival Name" },
    { key: "Day_Of_Week",          label: "Day" },
    { key: "Month",                label: "Month" },
    { key: "Year",                 label: "Year" },
  ], []);

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="rounded-2xl shadow-lg overflow-hidden" style={{ background: 'linear-gradient(135deg, #2d3440 0%, #535c6b 100%)' }}>
        <div className="px-4 sm:px-6 py-4 sm:py-5 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-mlb-gold/20 border border-mlb-gold/30 flex items-center justify-center flex-shrink-0">
              <CalendarDays className="w-4 h-4 sm:w-5 sm:h-5 text-mlb-gold" />
            </div>
            <div className="min-w-0">
              <h2 className="text-xl sm:text-2xl font-extrabold text-white tracking-tight">Holidays</h2>
              <p className="text-white/60 text-xs sm:text-sm mt-0.5">Public holidays and festivals</p>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            <select
              value={yearFilter}
              onChange={e => setYearFilter(Number(e.target.value))}
              className="px-2.5 sm:px-3 py-2 rounded-lg text-sm font-semibold bg-white/10 text-white border border-white/20 focus:outline-none focus:bg-white/20"
            >
              {YEAR_OPTIONS.map(y => <option key={y} value={y} className="text-gray-900">{y}</option>)}
            </select>
            {showAdd && (
              <button
                onClick={openAdd}
                className="flex items-center gap-1.5 sm:gap-2 bg-mlb-gold hover:bg-mlb-gold/80 text-brand-700 font-bold py-2 px-3 sm:px-4 rounded-lg transition-all shadow-md text-sm"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden xs:inline">Add Holiday</span>
                <span className="xs:hidden">Add</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Card view — parent & delivery boy */}
      {viewOnly ? (
        loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-brand-500 border-t-transparent" />
          </div>
        ) : holidays.length === 0 ? (
          <div className="text-center py-20 text-gray-400">
            <CalendarDays className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="font-medium">No holidays found for {yearFilter}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {holidays.map((h, idx) => {
              const day = new Date(h.Date).getDate();
              return (
                <div
                  key={h.id ?? idx}
                  className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all overflow-hidden"
                >
                  {/* Coloured top strip */}
                  <div className="px-5 py-3 flex items-center justify-between" style={{ background: 'linear-gradient(135deg, #2d3440 0%, #535c6b 100%)' }}>
                    <div className="flex items-center gap-2">
                      <div className="w-9 h-9 bg-mlb-gold rounded-xl flex items-center justify-center shadow-sm">
                        <span className="text-brand-700 font-extrabold text-base leading-none">{day}</span>
                      </div>
                      <div>
                        <p className="text-white font-bold text-sm leading-tight">{h.Month}</p>
                        <p className="text-white/60 text-xs">{h.Year}</p>
                      </div>
                    </div>
                    <span className="text-xs font-semibold bg-mlb-gold/20 text-mlb-gold border border-mlb-gold/30 px-2.5 py-1 rounded-full">
                      {h.Day_Of_Week}
                    </span>
                  </div>
                  {/* Body */}
                  <div className="px-5 py-4 flex items-center gap-3">
                    <div className="w-8 h-8 bg-mlb-green/15 rounded-lg flex items-center justify-center flex-shrink-0">
                      <CalendarDays className="w-4 h-4 text-mlb-green" />
                    </div>
                    <p className="font-semibold text-gray-900 text-sm">{h.Name_Of_The_Festival}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )
      ) : (
        /* Table view — admin / superuser / branch incharge */
        <GenericTable
          title={`${yearFilter}`}
          columns={columns}
          data={holidays}
          loading={loading}
          emptyMessage="No holidays found for this year"
          onEdit={showEdit ? openEdit : undefined}
          onDelete={showDelete ? setDeleteTarget : undefined}
        />
      )}

      {/* Add / Edit Modal */}
      <MasterModal
        isOpen={isModalOpen}
        title={editingItem ? "Edit Holiday" : "Add Holiday"}
        onClose={() => setIsModalOpen(false)}
        onSubmit={addTab === "excel" ? (e) => { e.preventDefault(); handleSubmit(); } : (e) => { e.preventDefault(); handleSubmit(); }}
        isLoading={methods.formState.isSubmitting}
      >
        {/* Tab switcher (add only) */}
        {!editingItem && (
          <div className="flex gap-4 mb-4 border-b border-gray-200">
            {(["form", "excel"] as const).map(tab => (
              <button
                key={tab}
                type="button"
                onClick={() => setAddTab(tab)}
                className={`pb-2 text-sm font-semibold capitalize border-b-2 transition-colors ${
                  addTab === tab ? "border-brand-500 text-brand-500" : "border-transparent text-gray-400 hover:text-gray-600"
                }`}
              >
                {tab === "form" ? "Manual Entry" : "Upload Excel"}
              </button>
            ))}
          </div>
        )}

        {addTab === "excel" ? (
          <div className="space-y-3">
            <p className="text-sm text-gray-500">Upload an Excel file with holiday records.</p>
            <div
              onClick={() => fileRef.current?.click()}
              className="border-2 border-dashed border-gray-300 hover:border-brand-400 rounded-xl p-8 text-center cursor-pointer transition-colors"
            >
              <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              {excelFile
                ? <p className="text-sm font-semibold text-brand-700">{excelFile.name}</p>
                : <p className="text-sm text-gray-500">Click to select Excel file (.xlsx, .xls)</p>
              }
            </div>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={e => setExcelFile(e.target.files?.[0] ?? null)}
            />
          </div>
        ) : (
          <FormProvider {...methods}>
            <div className="flex flex-col gap-4">
              <FormField
                control={methods.control}
                name="Date"
                label="Date"
                type="date"
                error={methods.formState.errors.Date?.message}
                onClear={() => methods.clearErrors("Date")}
                required
              />
              <FormField
                control={methods.control}
                name="Name_Of_The_Festival"
                label="Festival Name"
                placeholder="e.g. Ugadi"
                error={methods.formState.errors.Name_Of_The_Festival?.message}
                onClear={() => methods.clearErrors("Name_Of_The_Festival")}
                required
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={methods.control}
                  name="Day_Of_Week"
                  label="Day of Week"
                  isSelect
                  options={[{ value: "", label: "Select day" }, ...DAYS.map(d => ({ value: d, label: d }))]}
                  error={methods.formState.errors.Day_Of_Week?.message}
                  onClear={() => methods.clearErrors("Day_Of_Week")}
                  required
                />
                <FormField
                  control={methods.control}
                  name="Month"
                  label="Month"
                  isSelect
                  options={[{ value: "", label: "Select month" }, ...MONTHS.map(m => ({ value: m, label: m }))]}
                  error={methods.formState.errors.Month?.message}
                  onClear={() => methods.clearErrors("Month")}
                  required
                />
              </div>
              <FormField
                control={methods.control}
                name="Year"
                label="Year"
                isSelect
                options={YEAR_OPTIONS.map(y => ({ value: String(y), label: String(y) }))}
                error={methods.formState.errors.Year?.message}
                onClear={() => methods.clearErrors("Year")}
                required
              />
            </div>
          </FormProvider>
        )}
      </MasterModal>

      {/* Delete Confirm */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                <CalendarDays className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Delete Holiday</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete <strong>{deleteTarget.Name_Of_The_Festival}</strong> ({deleteTarget.Date})?
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white font-semibold py-2.5 rounded-xl transition-all"
              >
                {deleting ? "Deleting…" : "Delete"}
              </button>
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold py-2.5 rounded-xl transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
